/**
 * ClientAdminBridgePanel — Bridge health sidebar for ClientAdmin console.
 * Shows the current client's bridge(s) with live heartbeat status.
 * Polls every 20s. Includes user menu (profile + logout).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { UserProfile } from "@clerk/clerk-react";
import { useClerk, useUser } from "@clerk/react-router";
import { ChevronDown, LogOut01, RefreshCw01, Server01, User01, XClose } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { useNavigate } from "react-router-dom";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useClientAdminApi } from "@/services/api/client-admin-api";
import type { ClientAdminBridgeDto } from "@/types/api/client-admin";
import { cx } from "@/utils/cx";

const POLL_INTERVAL_MS = 20_000;
const DEAD_THRESHOLD_MS = 25_000;

function isAlive(lastHeartbeatAt: string | null): boolean {
    if (!lastHeartbeatAt) return false;
    return Date.now() - new Date(lastHeartbeatAt).getTime() < DEAD_THRESHOLD_MS;
}

function formatRefreshedAt(date: Date | null): string {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function BridgePanelSkeleton() {
    return (
        <div className="flex flex-col gap-1 px-2 py-2">
            {[1, 2].map((i) => (
                <div key={i} className="h-11 animate-pulse rounded-md bg-secondary" />
            ))}
        </div>
    );
}

interface BridgeRowProps {
    bridge: ClientAdminBridgeDto;
}

function BridgeRow({ bridge }: BridgeRowProps) {
    const alive = isAlive(bridge.lastHeartbeatAt);

    return (
        <div className="flex w-full items-start gap-2 rounded-md px-2 py-2">
            <span className={cx("mt-1 size-2 shrink-0 rounded-full", alive ? "bg-success-500" : "bg-error-500")} />
            <div className="min-w-0 flex-1">
                <p className="truncate text-xs leading-tight font-medium text-primary">{bridge.name}</p>
                <p className="truncate text-xs text-tertiary">{alive ? "Online" : "Offline"}</p>
            </div>
            <Badge color={bridge.environment === "Production" ? "brand" : "gray"} size="sm" type="pill-color">
                {bridge.environment === "Production" ? "Prod" : "Test"}
            </Badge>
        </div>
    );
}

export function ClientAdminBridgePanel() {
    const clientAdminApi = useClientAdminApi();
    const navigate = useNavigate();
    const { signOut } = useClerk();
    const { user } = useUser();
    const userName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User" : "User";
    const userInitials = user ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "U" : "U";

    const [bridges, setBridges] = useState<ClientAdminBridgeDto[]>([]);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);

    const fetchRef = useRef<(() => Promise<void>) | null>(null);

    const fetchBridges = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const result = await clientAdminApi.getBridges();
            setBridges(result.data);
            setRefreshedAt(new Date());
        } catch {
            // Silent — panel is non-critical
        } finally {
            setIsRefreshing(false);
            setIsFirstLoad(false);
        }
    }, [clientAdminApi]);

    useEffect(() => {
        fetchRef.current = fetchBridges;
    }, [fetchBridges]);

    useEffect(() => {
        fetchRef.current?.();
        const interval = setInterval(() => {
            fetchRef.current?.();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = useCallback(async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await signOut();
            navigate("/login", { replace: true });
        } catch (error) {
            console.error("Logout failed:", error);
            setIsLoggingOut(false);
        }
    }, [isLoggingOut, signOut, navigate]);

    return (
        <>
            <aside className="flex h-full w-64 shrink-0 flex-col border-r border-secondary bg-primary">
                {/* User header */}
                <div className="border-b border-secondary px-3 py-3">
                    <Dropdown.Root>
                        <AriaButton className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 outline-hidden transition duration-100 ease-linear hover:bg-secondary">
                            <Avatar size="xs" initials={userInitials} />
                            <span className="text-sm font-semibold text-primary">{userName}</span>
                            <ChevronDown className="size-3.5 text-fg-quaternary" />
                        </AriaButton>
                        <Dropdown.Popover>
                            <Dropdown.Menu>
                                <Dropdown.Item icon={User01} label="Profile" onAction={() => setShowProfileModal(true)} />
                                <Dropdown.Separator />
                                <Dropdown.Item icon={LogOut01} label="Log out" onAction={handleLogout} />
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown.Root>
                </div>

                {/* Bridges header */}
                <div className="flex items-center justify-between border-b border-secondary px-3 py-3">
                    <div className="flex items-center gap-1.5">
                        <Server01 className="size-4 text-tertiary" />
                        <span className="text-sm font-semibold text-primary">Bridge</span>
                    </div>
                    <button
                        onClick={() => fetchRef.current?.()}
                        disabled={isRefreshing}
                        className="rounded-md p-1 text-tertiary transition-colors hover:bg-secondary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:pointer-events-none"
                    >
                        <RefreshCw01 className={cx("size-3.5", isRefreshing && !isFirstLoad && "animate-spin")} />
                    </button>
                </div>

                {/* Refreshed at */}
                {refreshedAt && (
                    <div className="px-3 py-1">
                        <p className="text-xs text-tertiary">Updated {formatRefreshedAt(refreshedAt)}</p>
                    </div>
                )}

                {/* Bridge list */}
                <div className="flex-1 overflow-y-auto">
                    {isFirstLoad ? (
                        <BridgePanelSkeleton />
                    ) : bridges.length === 0 ? (
                        <p className="px-3 py-4 text-xs text-tertiary">No bridge registered.</p>
                    ) : (
                        <div className="flex flex-col gap-0.5 px-1 py-1">
                            {bridges.map((bridge) => (
                                <BridgeRow key={bridge.id} bridge={bridge} />
                            ))}
                        </div>
                    )}
                </div>
            </aside>

            {/* User Profile Modal */}
            {showProfileModal && (
                <ModalOverlay isOpen isDismissable onOpenChange={(open) => !open && setShowProfileModal(false)}>
                    <Modal className="max-w-[960px]">
                        <Dialog>
                            <div className="flex h-[min(90vh,800px)] w-full flex-col overflow-hidden rounded-xl border border-secondary bg-primary shadow-xl">
                                <div className="flex shrink-0 items-center justify-between border-b border-secondary px-6 py-4">
                                    <h2 className="text-lg font-semibold text-primary">Profile</h2>
                                    <Button color="tertiary" size="sm" iconLeading={XClose} onClick={() => setShowProfileModal(false)} aria-label="Close" />
                                </div>
                                <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                                    <UserProfile
                                        routing="hash"
                                        appearance={{
                                            elements: {
                                                rootBox: "w-full h-full",
                                                cardBox: "w-full h-full shadow-none",
                                                card: "shadow-none w-full h-full",
                                                scrollBox: "overflow-hidden",
                                                navbar: "border-r border-secondary",
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </>
    );
}
